import { useEffect, useState } from 'react';
import { differenceInYears, format } from 'date-fns';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  AlertCircle,
  Calendar,
  Clock,
  Mail,
  MapPin,
  Phone,
  Plus,
  Share2,
  Shield,
  User,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { usePatient } from '../api/patients-api';
import type { UploadedDocumentOut } from '../types';
import { Separator } from '@/components/ui/separator';
import { useEncounters } from '../api/encounters-api';
import { LoadingState } from '../components/LoadingState';
import { PatientAvatar } from '../components/PatientAvatar';
import { PregnancySection } from '../components/PregnancySection';
import { P, RequirePermission } from '@/features/permissions';
import { UnifiedTimeline } from '../components/UnifiedTimeline';
import { useUploadedDocuments } from '../api/uploaded-documents-api';
import { DocumentViewerSheet } from '../components/DocumentViewerSheet';
import { UploadDocumentDialog } from '../components/UploadDocumentDialog';
import { usePageHeaderSetter } from '@/components/layout/PageHeaderContext';
import { UploadedDocumentSheet } from '../components/UploadedDocumentSheet';
import { ShareDialog } from '../components/ShareDialog';
import { ShareLinksTab } from '../components/ShareLinksTab';
import { type GeneratedDocumentOut, useDocuments } from '../api/documents-api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export function PatientDetailPage() {
  const { patientId } = useParams({ strict: false }) as { patientId: string };
  const navigate = useNavigate();
  const setPageHeader = usePageHeaderSetter();
  const [activeTab, setActiveTab] = useState('timeline');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [selectedGeneratedDoc, setSelectedGeneratedDoc] = useState<GeneratedDocumentOut | null>(null);
  const [selectedUploadedDocId, setSelectedUploadedDocId] = useState<string | undefined>(undefined);

  const { data: patient, isLoading: loadingPatient } = usePatient(patientId);
  const { data: encountersData } = useEncounters(
    patientId ? { limit: 100, patient_id: patientId } : undefined,
  );
  const { data: documentsData } = useDocuments(
    patientId ? { limit: 100, patient_id: patientId } : undefined,
  );
  const { data: uploadedData } = useUploadedDocuments(
    patientId ? { limit: 100, patient_id: patientId } : undefined,
  );

  const encounters = encountersData?.data ?? [];
  const documents = documentsData?.data ?? [];
  const uploadedDocs = uploadedData?.data ?? [];

  const meta = patient?.metadata_;
  const firstName = meta?.first_name ?? '';
  const lastName = meta?.last_name ?? '';
  const displayName = lastName && firstName ? `${lastName}, ${firstName}` : lastName || firstName || 'Unknown';

  useEffect(() => {
    if (!patient || !meta) return;
    setPageHeader({
      title: `${firstName} ${lastName}`,
      actions: (
        <div className="flex items-center gap-2">
          <RequirePermission permission={P.SHARE_PATIENT}>
            <Button variant="outline" size="sm" onClick={() => setShareOpen(true)}>
              <Share2 className="mr-2 h-4 w-4" /> Share
            </Button>
          </RequirePermission>
          <RequirePermission permission={P.CREATE_ENCOUNTER}>
            <Button size="sm" onClick={() => navigate({ to: '/encounters/new', search: { patientId } })}>
              <Plus className="mr-2 h-4 w-4" />
              New Encounter
            </Button>
          </RequirePermission>
        </div>
      ),
    });
    return () => setPageHeader(null);
  }, [patient, meta, setPageHeader, navigate, firstName, lastName]);

  if (loadingPatient) return <LoadingState />;

  if (!patient) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        Patient not found.
      </div>
    );
  }

  const dob = meta?.date_of_birth;
  const age = dob ? differenceInYears(new Date(), new Date(dob)) : null;

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      {/* Patient Header Card */}
      <Card>
        <CardContent className="p-4 md:p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-6">
            <PatientAvatar size="lg" lastName={lastName} firstName={firstName} />
            <div className="flex-1 min-w-0">
              <div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-3">
                <h2 className="text-xl font-semibold">{displayName}</h2>
                <Badge variant="outline" className="w-fit font-mono text-xs">
                  {patient.medical_record_number}
                </Badge>
                {patient.patient_source && (
                  <Badge variant="secondary" className="w-fit text-xs">
                    {patient.patient_source}
                  </Badge>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                {age !== null && meta?.gender && (
                  <span className="flex items-center gap-1">
                    <User className="h-3.5 w-3.5" />
                    {age}y,{' '}
                    {meta.gender === 'M' ? 'Male' : meta.gender === 'F' ? 'Female' : 'Other'}
                  </span>
                )}
                {dob && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    DOB: {format(new Date(dob), 'MMM d, yyyy')}
                  </span>
                )}
                {meta?.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-3.5 w-3.5" />
                    {meta.phone}
                  </span>
                )}
                {meta?.email && (
                  <span className="flex items-center gap-1">
                    <Mail className="h-3.5 w-3.5" />
                    {meta.email}
                  </span>
                )}
              </div>
              {meta?.allergies && meta.allergies.length > 0 && (
                <div className="mt-2 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
                  <span className="text-sm font-medium text-red-700">Allergies:</span>
                  <div className="flex flex-wrap gap-1">
                    {meta.allergies.map((a, i) => (
                      <Badge key={i} className="text-xs" variant="destructive">
                        {a}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-4 text-center shrink-0">
              <div>
                <p className="text-2xl font-bold">{encounters.length}</p>
                <p className="text-xs text-muted-foreground">Encounters</p>
              </div>
              <Separator className="h-12" orientation="vertical" />
              <div>
                <p className="text-2xl font-bold">{documents.length}</p>
                <p className="text-xs text-muted-foreground">Documents</p>
              </div>
              <Separator className="h-12" orientation="vertical" />
              <div>
                <p className="text-2xl font-bold">{uploadedDocs.length}</p>
                <p className="text-xs text-muted-foreground">Uploaded</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="shares">Share links</TabsTrigger>
        </TabsList>

        {/* Timeline Tab (unified) */}
        <TabsContent className="mt-4" value="timeline">
          <UnifiedTimeline
            patientId={patientId}
            onUploadClick={() => setUploadDialogOpen(true)}
            onOpenGeneratedDocument={(doc) => setSelectedGeneratedDoc(doc)}
            onOpenUploadedDocument={(doc: UploadedDocumentOut) => setSelectedUploadedDocId(doc.id)}
          />
        </TabsContent>

        {/* Details Tab */}
        <TabsContent value="details" className="mt-4 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Demographics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <DetailRow
                  label="Full Name"
                  value={`${firstName} ${lastName}`.trim() || 'Unknown'}
                />
                {dob && (
                  <DetailRow
                    label="Date of Birth"
                    value={format(new Date(dob), 'MMMM d, yyyy')}
                  />
                )}
                {age !== null && <DetailRow label="Age" value={`${age} years`} />}
                {meta?.gender && (
                  <DetailRow
                    label="Gender"
                    value={
                      meta.gender === 'M' ? 'Male' : meta.gender === 'F' ? 'Female' : 'Other'
                    }
                  />
                )}
                {meta?.phone && <DetailRow label="Phone" value={meta.phone} />}
                {meta?.email && <DetailRow label="Email" value={meta.email} />}
                {meta?.address && (
                  <div className="flex gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{meta.address}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {meta?.insurance && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Insurance
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <DetailRow label="Provider" value={meta.insurance.provider} />
                  <DetailRow label="Policy Number" value={meta.insurance.policy_number} />
                  {meta.insurance.group_number && (
                    <DetailRow label="Group Number" value={meta.insurance.group_number} />
                  )}
                </CardContent>
              </Card>
            )}

            {meta?.emergency_contact && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Emergency Contact</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <DetailRow label="Name" value={meta.emergency_contact.name} />
                  <DetailRow label="Phone" value={meta.emergency_contact.phone} />
                  <DetailRow label="Relationship" value={meta.emergency_contact.relationship} />
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Record Info
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <DetailRow label="MRN" value={patient.medical_record_number} />
                {patient.patient_source && (
                  <DetailRow label="Source" value={patient.patient_source} />
                )}
                <DetailRow
                  label="Created"
                  value={format(new Date(patient.created_at), 'MMM d, yyyy')}
                />
                <DetailRow
                  label="Last Updated"
                  value={format(new Date(patient.updated_at), 'MMM d, yyyy')}
                />
              </CardContent>
            </Card>
          </div>

          <PregnancySection patientId={patientId} />
        </TabsContent>

        <TabsContent value="shares" className="mt-4">
          <ShareLinksTab patientId={patientId} />
        </TabsContent>
      </Tabs>

      {/* Upload dialog */}
      <UploadDocumentDialog
        patientId={patientId}
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
      />

      {/* Generated document viewer */}
      <DocumentViewerSheet
        open={!!selectedGeneratedDoc}
        document={selectedGeneratedDoc}
        onOpenChange={(open) => {
          if (!open) setSelectedGeneratedDoc(null);
        }}
      />

      {/* Uploaded document viewer */}
      <UploadedDocumentSheet
        patientId={patientId}
        open={!!selectedUploadedDocId}
        documentId={selectedUploadedDocId}
        onOpenChange={(open) => {
          if (!open) setSelectedUploadedDocId(undefined);
        }}
      />

      {/* Share dialog */}
      <ShareDialog
        patientId={patientId}
        open={shareOpen}
        onOpenChange={setShareOpen}
      />
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}
